import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import Button from 'components/UI/Buttons/BaseButton';
import TextField from 'components/UI/Inputs/TextField';
import DeleteDialog from 'components/UI/Dialogs/DeleteDialog';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';

import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useFormat } from 'src/lib/date-fns';
import PaymentEntity from 'src/db/entities/payment/PaymentEntity';
import ProformaInvoiceEntity, {
  PAID_STATUS_SOURCE,
} from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import {
  createPayment,
  deletePayment,
  makeSelectPaymentsByInvoice,
  updatePayment,
} from 'src/slices/paymentsSlice/paymentsSlice';
import {
  fromDateInputValue,
  getBalanceView,
  parsePaymentAmount,
  toDateInputValue,
} from 'src/slices/paymentsSlice/utils';
import { formatMoney } from './formatMoney';
import styles from './InvoicePayments.module.css';

interface Props {
  invoice: ProformaInvoiceEntity;
}

interface FormState {
  // null while adding a new payment
  uuid: string | null;
  amount: string;
  date: string;
}

const newFormState = (): FormState => ({
  uuid: null,
  amount: '',
  date: toDateInputValue(Date.now()),
});

/**
 * Payments received against an invoice (LAE-768): totals, the paid status with
 * its source, and the history with add / edit / delete. The server recalculates
 * the invoice on every change; the invoice prop follows it.
 */
export default function InvoicePayments({ invoice }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation('proforma-invoices-page');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const format = useFormat();

  const selectPayments = useMemo(makeSelectPaymentsByInvoice, []);
  const payments = useAppSelector((state) => selectPayments(state, invoice.uuid));

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<PaymentEntity | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const currency = invoice.settings?.currency;
  const money = (value: number) => formatMoney(value, currency, language);
  const balance = getBalanceView(invoice.balanceDue);

  const amountValue = form ? parsePaymentAmount(form.amount) : null;
  const dateValue = form ? fromDateInputValue(form.date) : null;

  const openAdd = () => {
    setShowErrors(false);
    setForm(newFormState());
  };

  const openEdit = (payment: PaymentEntity) => {
    setShowErrors(false);
    setForm({
      uuid: payment.uuid,
      amount: String(payment.amount),
      date: toDateInputValue(payment.receivedAt),
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form) {
      return;
    }
    if (amountValue === null || dateValue === null) {
      setShowErrors(true);
      return;
    }

    setSaving(true);
    try {
      if (form.uuid) {
        await dispatch(
          updatePayment({
            uuid: form.uuid,
            proformaInvoiceId: invoice.uuid,
            amount: amountValue,
            receivedAt: dateValue,
          })
        ).unwrap();
      } else {
        await dispatch(
          createPayment({
            proformaInvoiceId: invoice.uuid,
            amount: amountValue,
            receivedAt: dateValue,
          })
        ).unwrap();
      }
      enqueueSnackbar({
        message: t(form.uuid ? 'payments.notification.updated' : 'payments.notification.added'),
        variant: MessageType.Success,
      });
      setForm(null);
    } catch (error) {
      enqueueSnackbar({ message: t('common:message.server_error'), variant: MessageType.Error });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const payment = paymentToDelete;
    setPaymentToDelete(null);
    if (!payment) {
      return;
    }

    try {
      await dispatch(
        deletePayment({ uuid: payment.uuid, proformaInvoiceId: payment.proformaInvoiceId })
      ).unwrap();
      if (form?.uuid === payment.uuid) {
        setForm(null);
      }
      enqueueSnackbar({
        message: t('payments.notification.deleted'),
        variant: MessageType.Success,
      });
    } catch (error) {
      enqueueSnackbar({ message: t('common:message.server_error'), variant: MessageType.Error });
    }
  };

  return (
    <section className={styles.wrapper} aria-label={t('payments.title')}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('payments.title')}</h3>
        {!form && (
          <Button variant="outlined" onClick={openAdd}>
            {t('payments.actions.add')}
          </Button>
        )}
      </div>

      <dl className={styles.summary}>
        <dt>{t('payments.summary.total')}</dt>
        <dd>{invoice.priceWithTax != null ? money(invoice.priceWithTax) : '-'}</dd>
        <dt>{t('payments.summary.totalPaid')}</dt>
        <dd>{money(invoice.totalPaid)}</dd>
        {balance.kind === 'overpaid' ? (
          <>
            <dt>{t('payments.summary.overpaid')}</dt>
            <dd className={styles.overpaid}>{money(balance.amount)}</dd>
          </>
        ) : (
          <>
            <dt>{t('payments.summary.balanceDue')}</dt>
            <dd>{money(balance.amount)}</dd>
          </>
        )}
        <dt>{t('form.fields.status')}</dt>
        <dd>
          {t(`status_value.${invoice.status}`)}
          <div className={styles.source}>
            {invoice.paidStatusSource === PAID_STATUS_SOURCE.PAYMENTS
              ? t('payments.statusSource.payments')
              : t('payments.statusSource.manual')}
          </div>
        </dd>
      </dl>

      {form && (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <TextField
            label={t('payments.fields.amount')}
            value={form.amount}
            onChange={(event) => setForm({ ...form, amount: event.target.value })}
            type="text"
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
            error={showErrors && amountValue === null}
            helperText={
              showErrors && amountValue === null ? t('payments.validation.amount') : undefined
            }
          />
          <TextField
            label={t('payments.fields.date')}
            value={form.date}
            onChange={(event) => setForm({ ...form, date: event.target.value })}
            type="date"
            fullWidth
            error={showErrors && dateValue === null}
            helperText={
              showErrors && dateValue === null ? t('payments.validation.date') : undefined
            }
          />
          <div className={styles.formActions}>
            <Button variant="text" onClick={() => setForm(null)} disabled={saving}>
              {t('form.actions.cancel')}
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={saving}>
              {form.uuid ? t('payments.actions.save') : t('payments.actions.add')}
            </Button>
          </div>
        </form>
      )}

      {payments.length === 0 ? (
        <p className={styles.empty}>{t('payments.empty')}</p>
      ) : (
        <ul className={styles.list}>
          {payments.map((payment) => (
            <li key={payment.uuid} className={styles.item}>
              <span className={styles.itemDate}>
                {format(new Date(payment.receivedAt), 'd MMM yyyy')}
              </span>
              <span className={styles.itemAmount}>{money(payment.amount)}</span>
              <Button variant="text" onClick={() => openEdit(payment)} disabled={saving}>
                {t('payments.actions.edit')}
              </Button>
              <Button
                variant="text"
                color="error"
                onClick={() => setPaymentToDelete(payment)}
                disabled={saving}
              >
                {t('duplicates:Delete_action')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {paymentToDelete && (
        <DeleteDialog
          title={t('payments.deleteConfirm.title')}
          description={t('payments.deleteConfirm.description', {
            amount: money(paymentToDelete.amount),
            date: format(new Date(paymentToDelete.receivedAt), 'd MMM yyyy'),
          })}
          open
          onClose={() => setPaymentToDelete(null)}
          onSuccess={handleDelete}
          cancelButtonText={t('duplicates:Cancel_action')}
          deleteButtonText={t('duplicates:Delete_action')}
        />
      )}
    </section>
  );
}
